import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, Spinner } from '../../components/ui';
import { fullName, fmtDate, fmtTime, isPastFunction } from '../../utils/helpers';
import MemberForm from './MemberForm';
import QuickAdd from './QuickAdd';

// Desktop UI for Members — wide table.
export default function MembersDesktop({ vm }) {
  const {
    isAdmin, canAdd, canEdit, canDelete, mine, functions, invitableFunctions, loading, filtered, stats,
    q, setQ, fnFilter, setFnFilter, fnName, openAdd, openEdit, del, open,
    inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    isMarriage, sort, setSort, toggleSort, SORT_OPTIONS, quick,
    toggleRowFunction, rowHasFunction, savingFn, cardInfo, sideFilter, setSideFilter, sideCounts,
    cardFilter, setCardFilter, cardCounts, openFamilyFor,
  } = vm;

  // Right-click menu on a guest row: { x, y, row } or null.
  const [menu, setMenu] = useState(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', onKey, true); };
  }, [menu]);
  const openMenu = (e, row) => {
    e.preventDefault();
    setMenu({ x: Math.min(e.clientX, window.innerWidth - 260), y: Math.min(e.clientY, window.innerHeight - 190), row });
  };

  // Columns: only functions still to come — one that is over (e.g. the June
  // Cocktail Party) is not shown; its invitations stay saved.
  const tableFunctions = functions.filter(f => !isPastFunction(f));

  // The table header sticks right under the toolbar, whose height changes as
  // it wraps - so measure it into --guest-bar-h (used by .guests-page-desktop CSS).
  const pageRef = useRef(null);
  const barRef = useRef(null);
  useLayoutEffect(() => {
    const bar = barRef.current, page = pageRef.current;
    if (!bar || !page || typeof ResizeObserver === 'undefined') return undefined;
    const set = () => page.style.setProperty('--guest-bar-h', `${bar.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  // Column header that sorts on click; shows ▲/▼ on the active column.
  const Th = ({ k, children, className, title, rowSpan }) => (
    <th className={(className || '') + ' sortable' + (sort.key === k ? ' active' : '')} title={title || 'Sort'}
      rowSpan={rowSpan} onClick={() => toggleSort(k)}>
      {children}{sort.key === k && <span className="arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div className="guests-page guests-page-desktop" ref={pageRef}>
      <div className="guest-controls-sticky guest-toolbar" ref={barRef}>
        <div className="guest-toolbar-title"
          title={`${stats.parties} guests · ${stats.persons} persons expected${stats.entries !== stats.parties ? ` · ${stats.entries} function invites` : ''}${fnFilter ? ` for ${fnName(fnFilter)}` : ' (all functions)'}${isAdmin ? ' — full list across all members' : ' — yours plus Common ones'}`}>
          <h1>Guests</h1>
          <span className="guest-toolbar-sub"><strong>{stats.parties}</strong> · {stats.persons} pax</span>
        </div>
        {isMarriage && (
          <div className="side-toggle" role="group" aria-label="Show side">
            {[['', 'All', sideCounts.all], ['Groom', 'Groom', sideCounts.Groom], ['Bride', 'Bride', sideCounts.Bride]].map(([v, label, n]) => (
              <button key={label} type="button" className={'side-btn' + (v ? ` ${v.toLowerCase()}` : '') + (sideFilter === v ? ' on' : '')}
                onClick={() => setSideFilter(v)} title={v ? `Only ${label}-side guests` : 'Both sides'}>
                {label} <span>{n}</span>
              </button>
            ))}
          </div>
        )}
        <div className="side-toggle" role="group" aria-label="Show guests by card">
          {[['', 'All', cardCounts.all, 'All guests'],
            ['cards', 'Own+Head', cardCounts.cards, 'One row per invitation card: guests on their own card and family heads'],
            ['own', 'Own', cardCounts.own, 'Only guests on their own card (in no family)']].map(([v, label, n, tip]) => (
            <button key={label} type="button" className={'side-btn' + (cardFilter === v ? ' on' : '')} title={tip}
              onClick={() => setCardFilter(v)}>
              {label} <span>{n}</span>
            </button>
          ))}
        </div>
        <div className="guest-toolbar-filters">
          <select value={fnFilter} onChange={e => setFnFilter(e.target.value)} title="Filter by function">
            <option value="">All functions</option>
            {functions.map(f => <option key={f.FunctionId} value={f.FunctionId}>{f.FunctionName}</option>)}
          </select>
          <select value={sort.key} onChange={e => setSort(s => ({ key: e.target.value, dir: s.dir }))} title="Sort by">
            {SORT_OPTIONS.map(([k, l]) => <option key={k} value={k}>⇅ {l}</option>)}
          </select>
          <button className="btn light" title={sort.dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
            onClick={() => setSort(s => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))}>
            {sort.dir === 'asc' ? 'A→Z' : 'Z→A'}
          </button>
          <input type="search" placeholder="Search…" title="Search by name or number" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <div className="spacer" />
        {canAdd && (
          <div className="guest-toolbar-actions">
            <button className={'btn light' + (quick.quickOpen ? ' active' : '')} disabled={!invitableFunctions.length}
              title="Type many guests at once, one line each" onClick={quick.quickOpen ? quick.closeQuick : quick.openQuick}>
              ⚡ Quick
            </button>
            <button className="btn primary" onClick={() => openAdd('individual')} disabled={!invitableFunctions.length} title="Add one guest">+ Guest</button>
            <button className="btn light" onClick={() => openAdd('family')} disabled={!invitableFunctions.length} title="Add a family (one card)">+ Family</button>
          </div>
        )}
      </div>

      {functions.length > 0 && !invitableFunctions.length && (
        <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
          All functions of this event are over — guests can no longer be added.
        </div>
      )}
      {!functions.length && (
        <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
          No functions yet — an Event Admin must add functions before guests can be invited.
        </div>
      )}

      {quick.quickOpen && <QuickAdd vm={vm} />}

      <div className="card table-wrap guests-table-wrap">
        {loading ? <Spinner /> : filtered.length === 0 ? (
          <div className="empty">No guests yet.</div>
        ) : (
          <table className="tbl compact guests-table">
            {/* Fixed widths for the text columns; the function columns share
                whatever width is left, so the table fills the screen. */}
            <colgroup>
              <col style={{ width: 220 }} />
              <col style={{ width: 100 }} />
              {tableFunctions.map(f => <col key={f.FunctionId} />)}
              <col style={{ width: 180 }} />
              <col style={{ width: 64 }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                <Th k="name" className="g-name" rowSpan={2}>Guest</Th>
                <Th k="mobile" rowSpan={2}>Mobile</Th>
                {tableFunctions.length > 0 && <th className="fn-group" colSpan={tableFunctions.length}>Functions</th>}
                <th rowSpan={2} title="Whose invitation card this guest is on">Card</th>
                <Th k="rsvp" rowSpan={2}>RSVP</Th>
                <th rowSpan={2}></th>
              </tr>
              <tr className="fn-head-row">
                {tableFunctions.map(f => (
                  <th key={f.FunctionId} className={'fn-col' + (isPastFunction(f) ? ' past' : '')}
                    title={`${f.FunctionName} · ${fmtDate(f.FunctionDate)}${f.StartTime || f.FunctionTime ? ` · ${fmtTime(f.StartTime || f.FunctionTime)}` : ''}${isPastFunction(f) ? ' (over)' : ''}`}>
                    <span className="fn-col-name">{f.FunctionName}</span>
                    <span className="fn-col-date">{fmtDate(f.FunctionDate).slice(0, 5)}{f.StartTime ? ` ${f.StartTime}` : ''}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const editable = mine(row);
                // Second line under the name: whatever detail exists, joined compactly.
                const detail = [row.Relation, row.GroupName, row.City].filter(Boolean).join(' · ');
                const rsvp = row.RSVPStatus && row.RSVPStatus !== 'Pending'
                  ? `${row.RSVPStatus}${row.ConfirmedCount ? ` (${row.ConfirmedCount})` : ''}` : '—';
                const card = cardInfo(row);
                return (
                  <tr key={row.GuestId} onContextMenu={(e) => openMenu(e, row)} className={menu && menu.row.GuestId === row.GuestId ? 'ctx-on' : undefined}>
                    <td className="g-name">
                      <button type="button" className="g-name-btn" disabled={!(editable && canEdit)}
                        title={editable && canEdit ? 'Open this guest' : undefined} onClick={() => openEdit(row)}>
                        {fullName(row)}
                      </button>
                      {(row.Visibility || 'Private') === 'Private' && <span className="g-private" title="Private — only the adder and admins see this guest">🔒</span>}
                      {(detail || row.Remarks || Number(row.AddedByGuest) === 1) && (
                        <div className="sub" title={row.Remarks || undefined}>
                          {detail}{detail && row.Remarks ? ' · ' : ''}{row.Remarks ? <em>{row.Remarks}</em> : null}
                          {Number(row.AddedByGuest) === 1 && <span className="chip guest-added">Guest</span>}
                        </div>
                      )}
                    </td>
                    <td className="g-mobile">{row.MobileNumber || '—'}</td>
                    {tableFunctions.map(f => {
                      const on = rowHasFunction(row, f.FunctionId);
                      const past = isPastFunction(f);
                      const busy = savingFn === `${row.GuestId}:${f.FunctionId}`;
                      return (
                        <td key={f.FunctionId} className={'fn-col' + (on ? ' on' : '')}>
                          <input type="checkbox" checked={on} disabled={!(editable && canEdit) || past || !!savingFn}
                            className={busy ? 'saving' : undefined}
                            title={`${f.FunctionName}${past ? ' (over)' : ''}${on ? ' — invited' : ''}`}
                            onChange={() => toggleRowFunction(row, f.FunctionId)} />
                        </td>
                      );
                    })}
                    <td className="g-card">
                      <button type="button" className="g-card-btn" disabled={!(editable && canEdit)} onClick={() => openEdit(row)}
                        title="Change the card in the guest form">
                        {card.kind === 'head' ? <span className="chip ok">Head{card.size > 1 ? ` · ${card.size}` : ''}</span>
                          : card.kind === 'member' ? <span className="g-card-of">on {card.headName}</span>
                            : <span className="muted">own</span>}
                        {card.extra > 0 && <span className="chip warn" title="Extra cards">+{card.extra}</span>}
                      </button>
                    </td>
                    <td className="num">{rsvp}</td>
                    <td className="guest-row-actions">
                      <div className="guest-actions-inner">
                        {row.MobileNumber && (
                          <button className="btn primary sm invite-action-btn" title="Invite on WhatsApp" onClick={() => openInvite(row)}>
                            Invite
                          </button>
                        )}
                        {editable && canEdit && <button className="icon-btn" title="Edit" onClick={() => openEdit(row)}>✎</button>}
                        {editable && canDelete && <button className="icon-btn danger" title="Delete" onClick={() => del(row)}>🗑</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {menu && (() => {
        const r = menu.row;
        const editable = mine(r);
        const fam = Number(r.FamilyId || 0);
        const label = fam && fam === Number(r.GuestId) ? 'Add family members' : 'Add family members (make head)';
        return (
          <div className="ctx-menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}>
            <div className="ctx-title">{fullName(r)}</div>
            <button type="button" disabled={!(editable && canAdd)} onClick={() => { setMenu(null); openFamilyFor(r); }}>👪 {label}</button>
            <button type="button" disabled={!(editable && canEdit)} onClick={() => { setMenu(null); openEdit(r); }}>✎ Edit guest</button>
            <button type="button" disabled={!r.MobileNumber} onClick={() => { setMenu(null); openInvite(r); }}>💬 Invite on WhatsApp</button>
            <button type="button" className="danger" disabled={!(editable && canDelete)} onClick={() => { setMenu(null); del(r); }}>🗑 Delete</button>
          </div>
        );
      })()}

      {open && <MemberForm vm={vm} />}
      {inviteRow && (
        <Modal title="Invite on WhatsApp" onClose={closeInvite} footer={<button className="btn light" onClick={closeInvite} disabled={!!inviteSharing}>Cancel</button>}>
          <div style={{ display: 'grid', gap: 10 }}>
            {inviteCooldown > 0 && (
              <div className="invite-cooldown-note">
                Please wait {inviteCooldown} seconds before sending another invitation.
              </div>
            )}
            <button className="btn primary" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('image')}>
              {inviteSharing === 'image' ? 'Opening...' : 'Image Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('text')}>
              {inviteSharing === 'text' ? 'Opening...' : 'Text Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0 || !guestHasAllFunctions(inviteRow)} onClick={() => sendInvite('pdf')}>
              {inviteSharing === 'pdf' ? 'Opening...' : 'PDF Invite'}
            </button>
            {!guestHasAllFunctions(inviteRow) && (
              <div className="muted" style={{ fontSize: 12 }}>PDF invite is available only when this guest is invited to all functions.</div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
